import { isObservable, raw } from '@nx-js/observer-util';
import { classify } from 'inflected';
import addProxyInstanceOf from '../../javascripts/util/addProxyInstanceOf';
import { findPropertyDescriptor } from '../../javascripts/util/findPropertyDescriptor';
import { isPresent } from '../../javascripts/util/isPresent';
import { pick } from 'lodash';
import EnumValue from './EnumValue';
import Errors from './Errors';
import Fragment from './Fragment';
import { MutationBatcher } from './MutationBatcher';
import NullClient from './NullClient';
import Query, { type Selection, type Selections } from './Query';
import { type QueryBatcher } from './QueryBatcher';
import { type UpdateCallbacks } from './ReactiveModelRegistry';
import type ReactiveModelRegistry from './ReactiveModelRegistry';
import { RelationAttribute } from './RelationAttribute';
import SynchronizedModelRegistry from './SynchronizedModelRegistry';
import { type ApplicationModelClient } from './client';
import { generateModelJSON, modelAttribute, modelObject } from './modelBuilder';
import type Models from './models';
import {
  type ModelName,
  type RecordId,
  type RecordIdentifier,
  type RecordReferenceNum,
} from 'javascripts/shared-types';
import * as Sentry from '@sentry/browser';

// Global client instance.
let client = new NullClient();

let objectIdCounter = 0;

/**
 * type for using in place of an ApplicationModel constructor where needed
 */
export type ApplicationModelConstructor<T extends ApplicationModel> = {
  new (...args: any[]): T;
  typename: ModelName;
  internalTypename: string;
  terminologyName?: string;
  hasField(fieldName: string): boolean;
  filterType?: string;
  inputType?: string;
  fields: Record<string, Field>;
  filters: Record<string, string>;
  pathForId: (id: RecordId | RecordReferenceNum) => string | null;

  select(...attrs: Selection[] | Selections[]): Query<any, T>;
  selectAll(): Query<any, T>;
  where(filters): Query<any, T>;
  merge(subqueries): Query<any, T>;
  deepMerge(subqueries): Query<any, T>;
  idFromReactivePattern(reactivePattern: string): RecordId;

  readonly updateFragmentName: string;
  readonly createFragmentName: string;
};

/**
 * Wraps a GraphQL object, decorating it with other fields and helpful
 * accessors. The idea is that it should feel close to Rails, and
 * should use similar names and patterns where they make sense.
 *
 * To improve performance, attributes are kept in their primitive form
 * until they are accessed for the first time. For example, a deeply
 * nested object will not be wrapped in a model until it's
 * accessed. Bringing an attribute into its realized form is called
 * `instantiating`.
 */
export default class ApplicationModel {
  static typename: string = null;
  static internalTypename: string = null;

  static defaultQuery<M extends typeof ApplicationModel>(this: M) {
    let query = new Query(this);

    if (this.fields.id) {
      query = query.select('id');
    }

    return query;
  }

  /**
   * Starts a new query. This is usually what you want to use to
   * create Rails-like scopes.
   *
   * @param attrs The attributes to select in the
   * query.
   */
  static select<M extends typeof ApplicationModel>(
    this: M,
    ...attrs: Selection[] | Selections[]
  ) {
    return this.defaultQuery().select(...(attrs as any));
  }
  static selectAll<M extends typeof ApplicationModel>(this: M) {
    return this.defaultQuery().selectAll();
  }
  static where<M extends typeof ApplicationModel>(this: M, filters) {
    return this.defaultQuery().where(filters);
  }
  static merge<M extends typeof ApplicationModel>(this: M, subqueries) {
    return this.defaultQuery().merge(subqueries);
  }
  static deepMerge<M extends typeof ApplicationModel>(this: M, subqueries) {
    return this.defaultQuery().deepMerge(subqueries);
  }

  /**
   * Fetch *all* records, even if there are multiple
   * pages. `pageCallback` will be called for each page as it
   * arrives. If no callback is specified, will return all of the
   * records found.
   *
   * @param {Query} scope
   */
  static async fetchAll<M extends typeof ApplicationModel>(
    this: M,
    scope: Query<M, InstanceType<M>>,
    pageCallback = undefined
  ) {
    return scope.findInBatches(pageCallback);
  }

  /**
   * Creates a copy of a model that can be used as a "blank" object. A
   * "blank" object, when assigned to an assocation, will null out the
   * association.
   */
  static blank<M extends typeof ApplicationModel>(
    this: M,
    attrs = {},
    options = {}
  ) {
    const object = new this({ id: null, ...attrs }, options);
    return object as InstanceType<M>;
  }

  /**
   * Returns the current client for making GraphQL requests.
   */
  static get client(): ApplicationModelClient {
    return client;
  }

  static set client(newClient: ApplicationModelClient) {
    client = newClient;
  }

  /**
   * Return true if the given model is an instance of this model class:
   *
   * ```ts
   * if (Feature.is(model)) {
   *   // model is a Feature
   *   console.log(model.durationInTeamWorkflowStatus)
   * }
   * ```
   */
  static is<M extends ApplicationModelConstructor<any>>(
    this: M,
    model: ApplicationModel
  ): model is InstanceType<M> {
    return model.typename === this.typename;
  }

  static isModel(value: any) {
    if (value === null || value === undefined) {
      return false;
    } else if (Array.isArray(value)) {
      return value.some(o => this.isModel(o));
    } else if (value.__typename) {
      return true;
    } else if (value instanceof ApplicationModel) {
      return true;
    }
    return false;
  }

  static hasField(fieldName: string) {
    return fieldName in this.fields;
  }

  static reactivePatternToCacheId(pattern) {
    const [name, id] = pattern.split('-');
    return [name.replace('::', ''), id].join(':');
  }

  /**
   * Returns the id for the instance of this model referred to by
   * `pattern`. For example, calling this on `Feature-123` will
   * return `123`.
   */
  static idFromReactivePattern(pattern: string): RecordId {
    const match = pattern.match(new RegExp(`^${this.internalTypename}-(\\d+)`));
    return match?.[1] as RecordId;
  }

  /**
   * Hash of all defined models.
   */
  static models = {} as typeof Models;

  /**
   * The graphql type used for create and update attributes
   */
  static inputType: string = undefined;

  /**
   * Hash of fields defined on this model.
   */
  static fields: Record<string, Field> = undefined;

  /**
   * The graphql type for list* queries filters parameter
   */
  static filterType: string = undefined;

  /**
   * Hash of filters defined on this model.
   */
  static filters = {};

  static async query<T>(fragment: Fragment): Promise<T> {
    const resolvedFragment = fragment.resolve();

    return ApplicationModel.client.query(
      resolvedFragment.toDocument(fragment.name, 'query'),
      {
        variables: resolvedFragment.toVariables(),
      }
    );
  }

  /**
   * Used to build a standard error fragment
   */
  static buildErrorFragment() {
    return new Fragment('errors').fragment(
      new Fragment('attributes').attr(
        'name',
        'messages',
        'fullMessages',
        'codes'
      )
    );
  }

  /**
   * Turn the given fragment into a mutation query, add standard error
   * attributes and send to the API
   *
   * @returns The return data from the mutation
   */
  static async mutate<T extends Record<string, any>>(
    fragment: Fragment,
    options?: any
  ): Promise<Aha.RecordErrors & T> {
    const mutationFragment = fragment
      .fragment(ApplicationModel.buildErrorFragment())
      .resolve();

    if (options?.batcher instanceof MutationBatcher) {
      return options.batcher.mutate(mutationFragment);
    } else {
      return ApplicationModel.client.mutate(
        mutationFragment.toDocument(
          classify(mutationFragment.name),
          'mutation'
        ),
        { variables: mutationFragment.toVariables() }
      );
    }
  }

  static get updateFragmentName() {
    return `update${this.typename}`;
  }

  static get createFragmentName() {
    return `create${this.typename}`;
  }

  static pathForId(id: RecordId | RecordReferenceNum): string | null {
    switch (this.typename) {
      case 'Epic':
        return `/epics/${id}`;

      case 'Feature':
        return `/features/${id}`;

      case 'Goal':
        return `/strategic_imperatives/${id}`;

      case 'Idea':
        return `/ideas/${id}`;

      case 'IdeaTheme':
        return `/ideas/idea_themes/${id}`;

      case 'Initiative':
        return `/initiatives/${id}`;

      case 'KeyResult':
        return `/key_results/${id}`;

      case 'Page':
        return `/pages/${id}`;

      case 'PdlcStep':
        return `/steps/${id}`;

      case 'Release':
        return `/releases/${id}`;

      case 'ReleasePhase':
        return `/release_phases/${id}`;

      case 'Requirement':
        return `/requirements/${id}`;

      case 'Task':
        return `/tasks/${id}`;

      case 'Project':
        return `/projects/${id}`;

      case 'DiscoveryInterview':
        return `/discovery_interviews/${id}`;

      case 'DiscoveryFile':
        return `/discovery_files/${id}`;

      case 'Iteration':
        return `/develop/iterations/${id}`;

      default:
        return null;
    }
  }

  internalObjectId: number;
  attributes: Record<string, any> = {};
  local: Record<string, any> = {};
  options: { query?: Query<any, any> } = {};
  instantiatedAttributes: Record<string, any> = {};
  dirtyAttributes = new Set<string>();
  cacheId: string;
  query: Query<any, any>;
  errors: Errors | undefined;
  _parent: RelationAttribute;

  /**
   * Override this static method if `setAttribute` on a field containing this
   * model type needs to transform the value. For example, `Note` overrides this
   * so that when a string is written to a note field it is transformed into
   * `new Note({htmlBody: value})`
   */
  static onParentSet(parent: ApplicationModel, fieldName: string, value: any) {
    return value;
  }

  /**
   * Get the constructor in a way that typescript can understand
   */
  get modelConstructor() {
    return this.constructor as unknown as ApplicationModelConstructor<this>;
  }

  /**
   * Creates a new instance of this model with the specified
   * attributes.
   *
   * @param attributes Attributes used to construct the object
   * @param options
   * @param options.query A query object used to reselect attributes after saving
   * @param options.parent The parent of the model
   *
   * `options`:
   *   - `query`: A Query object used to reselect attributes after saving
   *   - `parent`: The parent of the model
   */
  constructor(
    attributes: Record<string, any> = {},
    options: { query?: Query<any, any>; parent?: RelationAttribute } = {}
  ) {
    this.internalObjectId = objectIdCounter++;
    this.options = options;
    this.cacheId = client.cacheId(attributes);

    this.query = new Query(this.modelConstructor).first();
    if (this.modelConstructor.hasField('id')) {
      this.query = this.query.select('id');
    }
    if (options.query) {
      let originalQuery = options.query;
      if (originalQuery.unions) {
        originalQuery = originalQuery.unions[this.typename];
        if (!originalQuery) {
          throw new Error(
            `${this.typename} returned but union only contained ${Object.keys(
              options.query.unions
            ).join(',')}`
          );
        }
      }

      this.mergeQuery(originalQuery);
    }

    if (options.parent) {
      this._parent = options.parent;
    }

    this.defineAccessors();
    this.resetAttributes(attributes);
    this.initialize();

    // Don't allow new properties to be added to this model. Prevents
    // accidentally treating attributes like properties. Only applies when
    // we are not testing so that we can still mock implementations.
    if (typeof jest === 'undefined') {
      Object.seal(this);
    }
  }

  private mergeQuery(query: this['query']) {
    this.query = this.query.select(query.attrs).merge(query.subqueries);
  }

  /** Override to add custom initialization */
  initialize() {}

  /**
   * @returns `true` if the object is an existing record, `false` otherwise.
   */
  get persisted() {
    return 'id' in this && !!this.id;
  }

  /**
   * @returns A guaranteed unique identifier for the record. Returns internalObjectId if id is falsy (unsaved).
   */
  get uniqueId() {
    return this.getAttribute('id') || this.internalObjectId;
  }

  get id(): RecordId {
    return this.getAttribute('id');
  }

  get typename(): ModelName {
    return this.modelConstructor.typename;
  }

  get internalTypename(): string {
    return this.modelConstructor.internalTypename;
  }

  get recordIdentifier(): RecordIdentifier {
    if (!this.persisted) {
      throw new Error('Cannot get recordIdentifier for unsaved record.');
    }

    return `${this.typename}-${this.id}`;
  }

  /**
   * Returns a shallow duplicate of this record.
   */
  dup(): this {
    // @ts-ignore
    return new this.constructor({ ...this.attributes }, { ...this.options });
  }

  /**
   * Dynamically define a getter and setter for each attribute of this model.
   *
   * NOTE: This will only create accessors for attributes that are
   * used to create an instance or selected in a query.
   */
  defineAccessors() {
    if (!this.modelConstructor.fields) {
      throw new Error(`Fields must be defined for model ${this.typename}`);
    }

    Object.keys(this.modelConstructor.fields).forEach(attribute => {
      const oldDescriptor = findPropertyDescriptor(this, attribute);

      const getter =
        oldDescriptor?.get ||
        function () {
          return this.getAttribute(attribute);
        };
      const setter =
        oldDescriptor?.set ||
        function (newValue) {
          if (typeof this.setAttribute !== 'function') {
            const prototype = Object.getPrototypeOf(this);

            Sentry.addBreadcrumb({
              category: 'ApplicationModel',
              message: 'setAttribute is not a function',
              data: {
                typename: this.typename,
                isObservable: isObservable(this),
                constructorName: this.constructor?.name,
                hasPrototype: isPresent(prototype),
                prototype: prototype,
                attribute,
                newValue,
              },
            });
          }

          return this.setAttribute(attribute, newValue);
        };

      Object.defineProperty(this, attribute, {
        get: getter,
        set: setter,
        configurable: true,
      });
    });
  }

  /**
   * @returns `false` if the attribute `name` refers to a primitive value, `true` otherwise.
   */
  isRelationship(name: string): boolean {
    const field = this.modelConstructor.fields[name];
    return field.type === 'belongsTo' || field.type === 'hasMany';
  }

  /**
   * @return `true` if the field is writeable, `false` otherwise.
   */
  isWriteable(name: string): boolean {
    const field = this.modelConstructor.fields[name];
    if (!field) return false;

    if (field.type === 'attr') {
      return Boolean(field.writable);
    } else {
      return field.writable || isPresent(field.writeShape);
    }
  }

  isAttributeLoaded(name: string): boolean {
    return name in this.query.attrs || name in this.query.subqueries;
  }

  /**
   * @return `true` if the field is a JSON attribute.
   */
  isJson(name: string): boolean {
    const field = this.modelConstructor.fields[name];
    if (!field) return false;

    return field.type === 'attr' && Boolean(field.json);
  }

  getAttribute(name: string) {
    // We lazily instantiate model instances as they are needed.
    // If we already have a model or array of models for this attr,
    // return it.
    if (name in this.instantiatedAttributes) {
      return this.instantiatedAttributes[name];
    } else {
      const rawAttr = this.attributes[name];
      if (typeof rawAttr === 'undefined') {
        // Don't trigger an update since nothing changed, we are simply caching the value.
        raw(this).instantiatedAttributes[name] = undefined;
        return undefined;
      }
      const options = {
        query: this.query.subqueries[name],
        parent: new RelationAttribute(this, name),
        json: this.isJson(name),
      };
      const value = modelAttribute(rawAttr, options);

      // Use raw here to prevent triggering an update. We are just
      // caching a resolved value.
      raw(this).instantiatedAttributes[name] = value;
      if (this.attributes[name] === undefined) {
        this.attributes[name] = value;
      }

      return value;
    }
  }

  /**
   * @returns The value of the attribute `name`, without unnecessarily instantiating it.
   */
  getAttributeWithoutInstantiating(name: string) {
    // return this.instantiatedAttributes[name] ?? this.attributes[name];
    if (name in this.instantiatedAttributes) {
      return this.instantiatedAttributes[name];
    }
    return this.attributes[name];
  }

  /**
   * Compares two values for purposes of dirty tracking.
   *
   * @param oldValue The existing value
   * @param newValue The value to be changed to
   * @returns `true` if the objects are equal, `false` otherwise.
   */
  isEqual(oldValue: any, newValue: any): boolean {
    // TODO: Better dirty tracking (based on real equality)
    return oldValue === newValue;
  }

  /**
   * Assigns the ID of an association, if it is different from the current value.
   * Allows for optimized setting to avoid marking an attribute as dirty.
   */
  setAssociation(name, association) {
    if (
      !this[name] ||
      !this.attributes[name] ||
      this.attributes[name].id !== association.id
    ) {
      this[name] = association;
    }
  }

  /**
   * Sets the attribute `name` to `value`. This function can be used
   * even if this object didn't declare a setter for the attribute
   * `name`.
   *
   * @param name The attribute name
   * @param value The new attribute value
   * @param flagDirty When true, will perform dirty checks for attributes
   */
  setAttribute(name: string, value: any, flagDirty = true) {
    const field = this.modelConstructor.fields[name];
    const typename = field && 'typename' in field && field.typename;
    const model: typeof ApplicationModel | undefined =
      typeof typename === 'string' && ApplicationModel.models[typename];

    if (model && 'onParentSet' in model) {
      value = model.onParentSet(this, name, value);
    }

    if (flagDirty) {
      if (name in this.instantiatedAttributes) {
        if (!this.isEqual(this.instantiatedAttributes[name], value)) {
          this.flagDirty(name);
        }
      } else if (!this.isEqual(this.attributes[name], value)) {
        this.flagDirty(name);
      }
    }

    this.instantiatedAttributes[name] = value;

    if (this.attributes[name] === undefined) {
      this.attributes[name] = value;
    }
  }

  setAttributes(attrs, flagDirty = true) {
    for (let [name, value] of Object.entries(attrs)) {
      delete this.attributes[name];
      this.setAttribute(name, value, flagDirty);
    }
  }

  /**
   * Flag an attribute as dirty / changed
   *
   * @param name The attribute name
   */
  flagDirty(name: string) {
    this.dirtyAttributes.add(name);
  }

  /**
   * @returns `true` if the object's attributes have been modified, `false` otherwise.
   */
  isDirty() {
    return this.dirtyAttributes.size > 0;
  }

  /**
   * @param name The attribute name to check
   * @returns `true` if the given attribute has been modeified
   */
  isAttributeDirty(name: string) {
    return this.dirtyAttributes.has(name);
  }

  /**
   * Restores the last saved value of the given attribute, and treats it as unchanged.
   *
   * @param name The attribute name to restore
   */
  restoreAttribute(name: string) {
    if (this.dirtyAttributes.has(name)) {
      delete this.instantiatedAttributes[name];
      this.dirtyAttributes.delete(name);
    }
  }

  /**
   * Resets the attributes on the model and clears the dirty attributes.
   * Optionally, only reset the attributes that were passed in, others that
   * were not reset will remain dirty.
   *
   * @param newAttrs The attributes to reset
   * @param errors The errors from a mutation, if any.
   * @param options Options
   * @param options.undirtyAllAttributes Mark all attributes as not dirty, defaults to true.
   */
  resetAttributes(
    newAttrs: {},
    errors = {},
    options: { undirtyAllAttributes?: boolean } = {}
  ) {
    const { undirtyAllAttributes = true } = options;

    this.errors = new Errors(errors);
    if (newAttrs) {
      // Update attributes that are not `isEqual` to the old attributes.
      Object.entries(newAttrs).forEach(([name, value]) => {
        if (!this.isEqual(this.getAttributeWithoutInstantiating(name), value)) {
          delete this.attributes[name];
          this.setAttribute(name, value, false);
          delete this.instantiatedAttributes[name];
        }

        if (!undirtyAllAttributes) {
          this.dirtyAttributes.delete(name);
        }
      });

      if (undirtyAllAttributes) {
        this.dirtyAttributes.clear();
      }
    }

    // For new records, flag all specified attributes as dirty so
    // they're all sent during save
    if (!this.persisted && newAttrs) {
      Object.entries(newAttrs).forEach(([name, value]) => {
        if (value !== undefined) {
          this.flagDirty(name);
        }
      });
    }
  }

  /**
   * Sends a GraphQL mutation request, updating the current object from the response.
   *
   * @param {Fragment} fragment
   * @param {object} options All data passed along to generate the mutation query.
   * @param {import('./MutationBatcher').MutationBatcher=} options.batcher A MutationBatcher object that will execute the mutation in a batch.
   * @param {Query=} options.query The Query object used to update data from the mutation response
   * @param {boolean=} options.resetQuery When true, the passed in query will replace the model's current query. Defaults to false.
   * @param {boolean=} options.reset Whether to reset the attributes or not, defaults to true.
   *
   * @returns `true` if the mutation ran without errors, `false` otherwise.
   */
  async mutate(
    fragment: Fragment,
    options: {
      query?: Query<any, any>;
      reset?: boolean;
      resetQuery?: boolean;
      batcher?: MutationBatcher;
    } = {}
  ): Promise<boolean> {
    const { reset = true, resetQuery = true } = options;
    const query = options.query || this.query;
    let executer;
    const batcher = options.batcher;
    if (batcher instanceof MutationBatcher) {
      executer = fragment => batcher.mutate(fragment);
    } else {
      executer = fragment => ApplicationModel.mutate(fragment);
    }
    const data = await executer(fragment.fragment(query.buildQueryFragment()));

    if (resetQuery) {
      this.query = query;
    }
    const queryName = query.queryName;

    const mutationData = data[fragment.name];
    const errors = new Errors(mutationData.errors);

    if (reset && mutationData[queryName]) {
      const newAttributes = modelObject(mutationData[queryName]);
      this.resetAttributes(newAttributes as {}, mutationData.errors);
    } else if (reset && !errors.isEmpty) {
      this.errors = errors;
    }

    if (this.errors) return this.errors.isEmpty;
    return true;
  }

  /**
   * Converts an attribute value ready for graphql mutation mutation
   */
  prepareAttributeForQuery(key: string): any {
    const value = this.getAttributeWithoutInstantiating(key);
    const methodName = `${key}ForQuery`;

    if (typeof this[methodName] === 'function') {
      return this[methodName](value);
    } else if (typeof value?.prepareForQuery === 'function') {
      return value.prepareForQuery();
    } else if (this.isRelationship(key)) {
      if (!value) {
        return value;
      }

      const field = this.modelConstructor.fields[key];

      if (typeof value.valueForParentMutation === 'function') {
        return value.valueForParentMutation(this);
      }

      if (field && 'writeShape' in field && field.writeShape) {
        return field.writeShape.reduce((acc, shapeKey) => {
          // Skip undefined, but allow null so that a record can be removed from
          // an association
          if (value[shapeKey] === undefined) return acc;
          return { ...acc, [shapeKey]: value[shapeKey] };
        }, {});
      }
      // Handle belongsTo attributes where the attribute provided isn't an object,
      // eg. updating an estimate
      if (typeof value === 'object' && !Array.isArray(value) && 'id' in value) {
        // We will use the 'id' field if it is `null` so that a record can be
        // removed from an association.
        return { id: value.id };
      } else {
        return value;
      }
    } else if (value instanceof EnumValue) {
      return value.value;
    } else {
      return value;
    }
  }

  /**
   * Converts the attributes in `attributeKeys` to attributes that can
   * be interpolated into a GraphQL arguments list. For example, will
   * deep-convert arrays and objects, will wrap strings in "".
   *
   * @param {string[]} attributeKeys A list of attributes to convert
   *
   * @returns {Record<string, any>} An object with the converted attributes object.
   */
  prepareAttributesForQuery(attributeKeys) {
    return attributeKeys.reduce(
      (acc, key) => ({ ...acc, [key]: this.prepareAttributeForQuery(key) }),
      {}
    );
  }

  /**
   * Updates attributes on the model and then calls save.
   *
   * @param {object} attributes The attributes to set on the model before saving.
   * @param {object} options Options to pass to the save method.
   *
   * @returns {Promise<boolean>} `true` if the mutation ran without errors, `false` otherwise.
   */
  async update(attributes, options = {}) {
    Object.assign(this, attributes);

    return this.save(options);
  }

  /**
   * Updates or creates this record using a GraphQL mutation. Will use the
   * `update{ModelName}` or `create{ModelName}` mutations, respectively. Sends
   * all changed attributes and relationships as arguments, and, by default,
   * updates attributes using the query used to construct this object.
   *
   * @param {object} options Data used to modify the mutation query.
   * @param {object=} options.attributes Attributes to set before calling save.
   * @param {object=} options.args Bare, top-level (non-attribute) arguments passed along with the mutation. These will be at the same level as `id`, for example.
   * @param {Query=} options.query A Query object used to override the default query.
   * @param {import('./MutationBatcher').MutationBatcher=} options.batcher A MutationBatcher object that will execute the mutation in a batch.
   * @param {boolean=} options.resetQuery When true, a passed in query will replace the model's current query. Defaults to false.
   * @param {boolean=} options.always When true, will always perform the mutation even if no data has changed. Useful for forcing the object's attributes to update, even when there's nothing to save.
   *
   * @returns {Promise<boolean>} `true` if the mutation ran without errors, `false` otherwise.
   */
  async save(
    options: {
      attributes?: {};
      args?: {};
      query?: Query<any, any>;
      resetQuery?: boolean;
      always?: boolean;
      batcher?: MutationBatcher;
    } = {}
  ): Promise<boolean> {
    if (!this.isDirty() && !options.always) return true;

    let fragment = this.persisted
      ? new Fragment(this.modelConstructor.updateFragmentName).argument(
          'id',
          this.id
        )
      : new Fragment(this.modelConstructor.createFragmentName);

    const args = {
      attributes: this.prepareAttributesForQuery(
        Array.from(this.dirtyAttributes)
      ),
      ...options.args,
    };

    Object.entries(args).forEach(([key, value]) => {
      fragment = fragment.argument(
        key,
        value,
        key === 'attributes' ? this.modelConstructor.inputType : null
      );
    });

    const attributesToSync = Array.from(this.dirtyAttributes).reduce(
      (res, key) => ({
        ...res,
        [key]: this.getAttributeWithoutInstantiating(key),
      }),
      {}
    );
    SynchronizedModelRegistry.instance.synchronize(this, attributesToSync);

    return this.mutate(
      fragment,
      pick(options, 'query', 'resetQuery', 'batcher')
    );
  }

  /**
   * Reload the model from the API. If a query option is provided then that
   * query will be used for the reload.
   */
  async reload(
    options: {
      query?: Query<any, any>;
      batcher?: QueryBatcher;
    } = {}
  ) {
    if (!this.persisted) {
      throw new Error('Cannot reload unsaved record.');
    }

    const query: Query<any, typeof this> = options.query || this.query;

    let newRecord: this | null;

    if (options.batcher) {
      if (this.modelConstructor.filters['id']) {
        newRecord = await options.batcher.findBy(query, {
          id: this.id,
        });
      } else {
        newRecord = await options.batcher.find(query, this.id);
      }
    } else {
      newRecord = await query.find(this.id);
    }

    if (newRecord) {
      this.resetAttributes(newRecord.attributes);
    }

    if (options.query) {
      this.mergeQuery(options.query);
    }
  }

  /**
   * Reverts any dirty attributes to their original values.
   */
  revert() {
    this.instantiatedAttributes = {};
    this.resetAttributes({});
  }

  /**
   * Load additional attributes from the API:
   *
   * ```
   * const feature = await Feature.select('referenceNum').find('FEAT-123');
   * feature.name => null
   * await feature.loadAttributes('name');
   * feature.name => 'Feature 123'
   * ```
   *
   * @param  {...any} attributes
   */
  async loadAttributes(...attributes) {
    await this.reload({
      query: this.query.select(attributes),
    });
  }

  /**
   * Destroys this record using the `delete{ModelName}` GraphQL
   * mutation.
   *
   * @returns `true` if the mutation ran without errors, `false` otherwise.
   */
  destroy(options: { args?: {} } = {}): Promise<boolean> {
    if (!this.persisted) {
      throw new Error("Can't destroy a non-persisted model");
    }

    let fragment = new Fragment(`delete${this.typename}`);

    const args = {
      id: this.id,
      ...options.args,
    };

    Object.entries(args).forEach(([key, value]) => {
      fragment = fragment.argument(key, value);
    });

    return this.mutate(fragment);
  }

  /**
   * Indicate that this model instance should synchronize will all other
   * instances for the same record.
   */
  synchronized() {
    SynchronizedModelRegistry.instance.register(this);
  }

  /**
   * @returns The reactiveId for a record, in the pattern 'Model-1234'
   */
  get reactiveId(): string {
    if (!this.persisted) {
      return;
    }

    return [this.internalTypename, this.id].join('-');
  }

  static fromJSON(json: any) {
    return modelAttribute(json);
  }

  toJSON() {
    return generateModelJSON(this);
  }

  /**
   * Register model for reactive updates
   *
   * @param callbacks
   */
  reactive<
    ChildTypeUnion extends ApplicationModelConstructor<any> = ApplicationModelConstructor<any>
  >(callbacks: UpdateCallbacks<this, ChildTypeUnion>) {
    if (aha && 'reactiveModelRegistry' in aha) {
      const registry = aha.reactiveModelRegistry as ReactiveModelRegistry;
      registry.register(this, callbacks);
    }
  }

  /**
   * Stop receiving reactive updates for this model
   */
  unreactive() {
    if (aha && 'reactiveModelRegistry' in aha) {
      const registry = aha.reactiveModelRegistry as ReactiveModelRegistry;
      registry.deregister(this);
    }
  }

  /**
   * Extends instances of this model with the properties defined in
   * `mixin`. Will overwrite any existitng properties of the same
   * name.
   */
  static extend(mixin) {
    Object.keys(mixin).forEach(key => {
      const descriptor = Object.getOwnPropertyDescriptor(mixin, key);
      Object.defineProperty(this.prototype, key, descriptor);
    });
  }
}

interface Attr {
  type: 'attr';
  json?: boolean;
  args?: Record<string, any>;
  readable?: boolean;
  writable?: boolean;
}

interface BelongsTo {
  type: 'belongsTo';
  args?: Record<string, any>;
  readable?: boolean;
  writable?: boolean;
  writeShape?: string[];
  typename?: string | string[];
}
interface HasMany {
  type: 'hasMany';
  args?: Record<string, any>;
  readable?: boolean;
  writable?: boolean;
  writeShape?: string[];
  typename?: string | string[];
}

type Field = Attr | BelongsTo | HasMany;

export function attr(options: Partial<Attr> = {}): Attr {
  return { type: 'attr', ...options };
}

export function belongsTo(options: Partial<BelongsTo> = {}): BelongsTo {
  return { type: 'belongsTo', ...options };
}
attr.belongsTo = belongsTo;

export function hasMany(options: Partial<HasMany> = {}): HasMany {
  return { type: 'hasMany', ...options };
}
attr.hasMany = hasMany;

addProxyInstanceOf(ApplicationModel);
